import { useCallback, useEffect, useReducer, useRef } from 'react';
import { audioPlayerReducer, initialState } from '@reducers/audioPlayerReducer';
import { STORAGE_KEYS } from '@constants/storageKeys';
import { SAVE_STATE_DEBOUNCE_MS } from '@constants/config';
import { useLocalStorage } from '@hooks/useLocalStorage';
import {
  deserializeAudioState,
  getEpisodeSavedTime,
  getPlayingEpisodeUrl,
  removeEpisodePosition,
  serializeAudioState,
  shouldSaveEpisodePosition
} from '@models/audio/audioModel';
import { readAudioState, writeAudioState } from '@models/audio/audioPersistence';

export function useAudioPlayerController() {
  const [state, dispatch] = useReducer(audioPlayerReducer, initialState);
  const audioRef = useRef(null);
  const saveTimeoutRef = useRef(null);

  const [episodePositions, setEpisodePositions] = useLocalStorage(
    STORAGE_KEYS.EPISODE_POSITIONS,
    {}
  );

  useEffect(() => {
    try {
      const restoredState = deserializeAudioState(readAudioState());
      if (restoredState) {
        dispatch({
          type: 'RESTORE_EPISODE',
          payload: restoredState
        });
      }
    } catch (error) {
      console.error('Error restoring audio state:', error);
    }
  }, []);

  useEffect(() => {
    if (!audioRef.current) return;

    if (state.pendingSeek) {
      audioRef.current.currentTime = state.currentTime;
      dispatch({ type: 'CLEAR_PENDING_SEEK' });
    }

    if (state.isPlaying) {
      const playPromise = audioRef.current.play();
      if (playPromise) {
        playPromise.catch((error) => {
          console.error('Play failed:', error);
          dispatch({ type: 'PAUSE' });
        });
      }
    } else {
      audioRef.current.pause();
    }

    if (!Number.isNaN(state.volume)) {
      audioRef.current.volume = Math.max(0, Math.min(1, state.volume));
    }

    if (!Number.isNaN(state.playbackRate)) {
      audioRef.current.playbackRate = state.playbackRate;
    }
  }, [
    state.currentTime,
    state.isPlaying,
    state.pendingSeek,
    state.volume,
    state.playbackRate
  ]);

  const handleTimeUpdate = useCallback(() => {
    if (audioRef.current) {
      dispatch({
        type: 'TIME_UPDATE',
        payload: { time: audioRef.current.currentTime }
      });
    }
  }, []);

  const handleLoadedMetadata = useCallback(() => {
    if (audioRef.current) {
      dispatch({
        type: 'SET_DURATION',
        payload: { duration: audioRef.current.duration }
      });
    }
  }, []);

  const handleEnded = useCallback(() => {
    dispatch({ type: 'ENDED' });

    if (state.currentEpisode) {
      setEpisodePositions((positions) =>
        removeEpisodePosition(positions, state.currentEpisode.url)
      );
    }
  }, [state.currentEpisode, setEpisodePositions]);

  useEffect(() => {
    if (!state.currentEpisode) return undefined;

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      try {
        const stateToSave = serializeAudioState(state);
        if (stateToSave) {
          writeAudioState(stateToSave);
        }

        if (shouldSaveEpisodePosition(state.duration, state.currentTime)) {
          setEpisodePositions((positions) => ({
            ...positions,
            [state.currentEpisode.url]: state.currentTime
          }));
        }
      } catch (error) {
        console.error('Error saving audio state:', error);
      }
    }, SAVE_STATE_DEBOUNCE_MS);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [
    state.currentEpisode,
    state.currentTime,
    state.volume,
    state.playbackRate,
    state.duration,
    setEpisodePositions
  ]);

  const loadEpisode = useCallback((episode) => {
    dispatch({
      type: 'LOAD_EPISODE',
      payload: {
        episode,
        savedTime: getEpisodeSavedTime(episodePositions, episode)
      }
    });
  }, [episodePositions]);

  const playEpisode = useCallback((episode) => {
    dispatch({
      type: 'PLAY_EPISODE',
      payload: {
        episode,
        savedTime: getEpisodeSavedTime(episodePositions, episode)
      }
    });
  }, [episodePositions]);

  const toggleEpisodePlayback = useCallback((episode) => {
    if (getPlayingEpisodeUrl(state.currentEpisode, state.isPlaying) === episode?.url) {
      dispatch({ type: 'PAUSE' });
      return;
    }

    playEpisode(episode);
  }, [playEpisode, state.currentEpisode, state.isPlaying]);

  const play = useCallback(() => dispatch({ type: 'PLAY' }), []);
  const pause = useCallback(() => dispatch({ type: 'PAUSE' }), []);
  const togglePlay = useCallback(() => dispatch({ type: 'TOGGLE_PLAY' }), []);
  const seekTo = useCallback((time) => {
    dispatch({ type: 'SEEK', payload: { time } });
  }, []);
  const setVolume = useCallback((volume) => {
    dispatch({ type: 'SET_VOLUME', payload: { volume } });
  }, []);
  const setPlaybackRate = useCallback((rate) => {
    dispatch({ type: 'SET_PLAYBACK_RATE', payload: { rate } });
  }, []);
  const skip = useCallback((seconds) => {
    dispatch({ type: 'SKIP', payload: { seconds } });
  }, []);

  return {
    currentEpisode: state.currentEpisode,
    isPlaying: state.isPlaying,
    playingEpisodeUrl: getPlayingEpisodeUrl(state.currentEpisode, state.isPlaying),
    currentTime: state.currentTime,
    duration: state.duration,
    volume: state.volume,
    playbackRate: state.playbackRate,
    episodePositions,
    restoredEpisodeUrl: state.restoredEpisodeUrl,
    loadEpisode,
    playEpisode,
    toggleEpisodePlayback,
    play,
    pause,
    togglePlay,
    seekTo,
    setVolume,
    setPlaybackRate,
    skip,
    audioRef,
    handleTimeUpdate,
    handleLoadedMetadata,
    handleEnded
  };
}
